import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { observer } from 'mobx-react-lite'
import { ColumnDef } from '@tanstack/react-table'
import { XmarkCircle } from 'iconoir-react'
import { DateComponent } from 'app/components/DateComponent'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/PageHeader'
import TableLayout from 'app/components/table/TableLayout'
import { OpenLinkCell } from 'app/components/table/OpenLinkCell'
import { LocationJson } from 'app/types/entities'
import type { UseAiAgentIndexReturn } from './useAiAgentIndex'

export function defaultColumns(
  aiCalls: Record<string, { count: number; last?: string }>,
  aiScheduledAppointments: Record<string, { count: number; last?: string }>,
  getLocationHref: (locationId: string) => string,
): ColumnDef<LocationJson, any>[] {
  return [
    {
      id: `Location Name`,
      accessorKey: `name`,
      header: `Location Name`,
      meta: {
        defaultVisible: true,
        pinned: true,
        sortKey: `name.keyword`,
        // A controlled cell fills the whole cell, so the hover Open button sits at its right edge.
        ControlledComponent: (row) => (
          <OpenLinkCell
            href={getLocationHref(row?.id ?? ``)}
            label={row?.name ?? ``}
            disabled={!row?.products?.includes(`AI Agent`)}
          />
        ),
      },
    },
    {
      id: `Agent Enabled`,
      header: `Agent Enabled`,
      cell: (props) => (
        <span>
          {props.row.original.products?.includes(`AI Agent`) ? (
            <CheckCircleIcon className="h-5 w-5 fill-current text-blue-400" />
          ) : (
            <XmarkCircle className="h-5 w-5 text-red-400" />
          )}
        </span>
      ),
      meta: {
        defaultVisible: true,
      },
      enableSorting: false,
    },
    {
      id: `AI Calls`,
      header: `AI Calls`,
      enableSorting: true,
      cell: (props) => <span>{aiCalls[props.row.original.id!]?.count ?? 0}</span>,
      meta: {
        defaultVisible: true,
      },
    },
    {
      id: `Last AI Call`,
      header: `Last AI Call`,
      enableSorting: true,
      cell: (props) => DateComponent({ value: aiCalls[props.row.original.id!]?.last, dateOnly: true }),
      meta: {
        defaultVisible: true,
      },
    },
    {
      id: `AI Appointments Scheduled`,
      header: `AI Appointments Scheduled`,
      enableSorting: true,
      cell: (props) => <span>{aiScheduledAppointments[props.row.original.id!]?.count ?? 0}</span>,
      meta: {
        defaultVisible: true,
      },
    },
    {
      id: `Last AI Appointment Scheduled`,
      header: `Last AI Appointment Scheduled`,
      enableSorting: true,
      cell: (props) => DateComponent({ value: aiScheduledAppointments[props.row.original.id!]?.last, dateOnly: true }),
      meta: {
        defaultVisible: true,
      },
    },
  ]
}

export type AiAgentIndexComponentProps = UseAiAgentIndexReturn

export const AiAgentIndexComponent = observer(function AiAgentIndexComponent({
  data,
  columns,
}: AiAgentIndexComponentProps) {
  return (
    <PageScroll variant="table" header={<PageHeader title="AI Agent" totalItems={data.locations?.total} />}>
      <TableLayout data={data.locations} columns={columns as any} edgePadded />
    </PageScroll>
  )
})
